import { useEffect, useState } from 'react';
import { removeImageMatte } from '../../utils/imageMatte';

export interface TransparentImageProps {
  src: string;
  alt: string;
  className?: string;
  /** Passed straight through — set on decorative images. */
  'aria-hidden'?: boolean | 'true' | 'false';
  /**
   * Knock the flat background out of the image so it sits on colored artwork
   * without a rectangle around it. Default true; turn it off where the image
   * is meant to keep its own background (a photo, a framed thumbnail).
   */
  knockOutMatte?: boolean;
}

/**
 * An `<img>` that renders logo artwork without the background rectangle a
 * flattened export leaves behind — the same logo file therefore works on a wax
 * seal, a themed page, and a white card.
 *
 * The original src is shown immediately and swapped for the processed version
 * once it is ready, so nothing ever blocks on the canvas work; images that are
 * already transparent (or cannot be read) simply keep rendering as they are.
 */
export function TransparentImage({
  src,
  alt,
  className,
  knockOutMatte = true,
  ...rest
}: TransparentImageProps) {
  const [resolvedSrc, setResolvedSrc] = useState(src);

  useEffect(() => {
    setResolvedSrc(src);
    if (!knockOutMatte || !src) return;
    let cancelled = false;
    void removeImageMatte(src).then((processed) => {
      if (!cancelled && processed) setResolvedSrc(processed);
    });
    return () => {
      cancelled = true;
    };
  }, [src, knockOutMatte]);

  return <img src={resolvedSrc} alt={alt} className={className} {...rest} />;
}
